import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Grid,
    Modal,
    NativeSelect,
    Tooltip,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';

import { addressesApi } from 'api/addresses';
import { catalogApi } from 'api/catalog';
import { logisticsApi } from 'api/logistics';
import { procurementApi as api } from 'api/procurement';
import { receivingApi } from 'api/receiving';
import { warehouseApi } from 'api/warehouse';
import {
    getExpirationDateFieldStyle,
    getProductionDateFieldStyle,
    isExpirationDateDisabled,
    isExpirationDateRequired,
    isProductionDateDisabled,
    isProductionDateRequired,
} from 'components/procurement/utils/dateFieldHelpers';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications, SearchBar } from 'shared';
import { dateHelper } from 'shared/utils/dateHelper';
import dayjs from 'utils/dayjsConfig';

import { CreatePO } from './createPurchaseOrder';
import PurchaseOrderGuardrail from './purchaseOrderGuardrail';

const PO_PREVENT_VENDOR_SEND = 200_000;

// The table to show items
function ViewItemsTable({
    countries,
    internalTransfers = false,
    parentRow,
    purchaseTypes,
    reasonCodes,
    receivingPOID = null,
    setExpandedRow,
    showReasonCodeModal,
    sourcingReasons,
    unpurchasedOutcomes,
}) {
    // Set PO ID and determine whether this is coming from the
    // receiving module or PO module
    const [poID, setPOID] = useState(null);
    const [fromReceiving, setFromReceiving] = useState(false);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [itemMaster, setItemMaster] = useState([]);
    const [itemMasterOptions, setItemMasterOptions] = useState([]);
    const [externalSkuOptions, setExternalSkuOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [purchaseOpts, setPurchaseOpts] = useState(purchaseTypes);
    const [srpEditable, setSrpEditable] = useState();
    const [rowID, setRowID] = useState(null);
    const [existingWeek, setExistingWeek] = useState(null);
    const [multiTruckPO, setMultiTruckPO] = useState(false);

    const getItemMaster = async () => {
        const itemMasterRes = await warehouseApi.getItemMaster(true, false);
        if (itemMasterRes.status === false) {
        } else if (!itemMasterRes.data || !itemMasterRes.data.rows) {
            setNotification({ text: 'Cant find any items', level: 'error' });
        } else {
            const items = itemMasterRes.data.rows.map((item) => {
                if (item.companySource !== 'MISFITS_MARKET') {
                    return {
                        text: `${item.companySource} ${item.thirdPartyItemID}: ${item.itemNameInternal}`,
                        value: item.sku,
                        externalSku: item.externalSku,
                    };
                }
                return {
                    text: `SKU ${item.sku}: ${item.itemNameInternal}`,
                    value: item.sku,
                    externalSku: item.externalSku,
                };
            });
            setItemMaster(itemMasterRes.data.rows);
            setItemMasterOptions(items);
        }
    };

    const getExternalSku = async () => {
        const result = await catalogApi.getItemInformation(true, 'itemMaster');

        if (result.status === false) return;

        setExternalSkuOptions(
            (result.data.items ?? []).map((item) => {
                const displayName = item.name ? item.name : 'NO NAME PROVIDED';
                return {
                    text: `SKU ${item.externalSku}: ${displayName}`,
                    value: item.externalSku,
                };
            }),
        );
    };

    const configurePOID = () => {
        if (parentRow != null) {
            setPOID(parentRow.original.id);

            setColumns([
                { accessor: 'lotID', Header: 'Lot', hide: !internalTransfers },
                { accessor: 'sku', Header: 'Item Master SKU' },
                { accessor: 'externalSku', Header: 'External SKU' },
                { accessor: 'itemName', Header: 'Name' },
                { accessor: 'fobTruckID', Header: 'Truck ID', hide: !multiTruckPO },
                {
                    accessor: 'expectedExpirationDate',
                    hide: internalTransfers,
                    Header: 'Expected Expiration',
                    editable: true,
                    editProps: {
                        type: 'input',
                        inputType: 'date',
                        disableTextInput: true,
                        required: true,
                    },
                },
                {
                    accessor: 'expectedProductionDate',
                    hide: internalTransfers,
                    Header: 'Expected Production',
                    editable: true,
                    editProps: {
                        type: 'input',
                        inputType: 'date',
                        disableTextInput: true,
                        required: true,
                    },
                },
                {
                    accessor: 'qty',
                    Header: 'Case Quantity',
                    editable: true,
                    editProps: {
                        type: 'input',
                        inputType: 'number',
                        required: true,
                    },
                },
                { Header: 'Number of Pallets', accessor: 'numPallets', editable: true, hide: !internalTransfers },
                { accessor: 'purchasingCasePackSize', Header: 'Pack Size', hide: internalTransfers },
                { accessor: 'vendorSku', Header: 'Vendor SKU', editable: !internalTransfers },
                { accessor: 'csPallet', Header: 'Cases per pallet', editable: !internalTransfers },
                { accessor: 'totalUnits', Header: 'Total Units', editable: false },
                { accessor: 'unitPrice', Header: 'Case Price', editable: !internalTransfers },
                { accessor: 'totalCost', Header: 'Total Costs', editable: false },
                { accessor: 'week', Header: 'Week', editable: true },
                { accessor: 'year', Header: 'Year', editable: true },
                {
                    accessor: 'purchaseType',
                    Header: 'Purchase Type',
                    editable: !internalTransfers,
                    editProps: { type: 'select', options: purchaseOpts },
                },
                {
                    accessor: 'suggestedRetailPrice',
                    Header: 'SRP',
                    editable: srpEditable,
                    editProps: {
                        type: 'input',
                        inputType: 'number',
                        required: false,
                    },
                },
                {
                    accessor: 'unpurchasedOutcome',
                    Header: 'Where Would It Go?',
                    editable: true,
                    editProps: {
                        type: 'select',
                        options: unpurchasedOutcomes,
                    },
                },
                {
                    accessor: 'sourcingReasons',
                    Header: "Why I'm a Misfit",
                    editable: true,
                    editProps: {
                        type: 'autocomplete-multiple',
                        options: sourcingReasons,
                        multiple: true,
                    },
                },
                {
                    accessor: 'countryOfOriginId',
                    Header: 'Country of Origin',
                    editable: true,
                    editProps: { type: 'select', options: countries },
                },
                {
                    accessor: 'remove',
                    Header: 'Remove',
                    Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                        <Button
                            id={`Remove_${row.id}`}
                            onClick={async () => {
                                showReasonCodeModal(
                                    'purchaseOrderMap',
                                    row.original.id,
                                    { poID, removeItem: true },
                                    getItemsInTable,
                                    parentRow.original.within36HoursOfPickup,
                                );
                            }}
                        >
                            Remove
                        </Button>
                    ),
                },
            ]);
        } else {
            setPOID(receivingPOID);
            setFromReceiving(true);

            setColumns([
                { accessor: 'sku', Header: 'Item Master SKU' },
                { accessor: 'itemName', Header: 'Name' },
                { accessor: 'expectedExpirationDate', Header: 'Expected Expiration' },
                {
                    accessor: 'qty',
                    Header: 'Quantity',
                    editable: true,
                    editProps: {
                        type: 'input',
                        inputType: 'number',
                        required: true,
                    },
                },
                {
                    accessor: 'clone',
                    Header: 'Clone',
                    Cell: ({ cell: { value: initialValue }, row }) => (
                        <Button
                            id={`Clone_${row.id}`}
                            onClick={async () => {
                                const response = await api.cloneItemOnPO(poID, row.original.id);

                                if (response.status === false) {
                                    setNotification({ text: `Can't clone item ${response.msg}`, level: 'error' });
                                    return;
                                }

                                getItemsInTable();
                            }}
                        >
                            Clone
                        </Button>
                    ),
                },
            ]);
        }
    };

    useEffect(() => {
        if (parentRow === undefined || internalTransfers) {
            setSrpEditable(false);
        } else {
            setSrpEditable(parentRow.original.status !== 'CANCELLED');
        }

        if (parentRow?.original?.truckID === 'PARTIAL') {
            setMultiTruckPO(true);
        }
    }, []);

    useEffect(() => {
        if (parentRow?.original?.id) {
            if (!!rowID && parentRow.original.id !== rowID) {
                setExpandedRow(null);
            }
            setRowID(parentRow.original.id);
        }
    }, [parentRow?.original?.id]);

    useEffect(() => {
        configurePOID();
        getItemMaster();
        getExternalSku();
    }, [poID, fromReceiving]);

    let getItemsInTable = async () => {
        if (poID == null) {
            return;
        }

        if (!rows.length) {
            setLoading(true);
        }

        const response = await api.getPurchaseOrderDetails(poID);

        if (response.status === false) {
            setNotification({ text: `No items found ${response.msg}`, level: 'error' });
            setRows([]);
            setLoading(false);
            return;
        }

        const poDetails = response.data.output;

        if (rows && rows.length > 0) {
            setExistingWeek(rows[0].week);
        } else if (poDetails.items && poDetails.items.length > 0 && poDetails.items[0].week) {
            // If rows is empty but we have items in the response, use the first item's week
            setExistingWeek(poDetails.items[0].week);
        }

        // Need to add id which will populate in saveEdit
        setRows(
            poDetails.items.map((row) => {
                row.id = row.poMapID;
                row.unitPrice = parseFloat(row.unitPrice).toFixed(2);
                row.numPallets = Math.floor(row.qty / row.csPallet);
                if (row.purchaseType === null) {
                    row.purchaseType = '';
                }
                row.totalUnits = row.qty * row.baseUnitsPerCase;
                row.totalCost = parseFloat(row.qty * row.unitPrice).toFixed(2);
                return row;
            }),
        );
        setLoading(false);
    };

    useEffect(() => {
        getItemsInTable();
    }, [poID]);

    return (
        <div>
            <Notifications options={notification} />
            {addItemModalOpen && (
                <>
                    {internalTransfers ? (
                        <AddLotsModal
                            parentRow={parentRow}
                            lotsAlreadyOnASN={rows}
                            poID={poID}
                            closeModal={() => {
                                setAddItemModalOpen(false);
                                getItemsInTable();
                            }}
                        />
                    ) : (
                        <AddItemModal
                            reasonCodes={reasonCodes}
                            parentRow={parentRow}
                            poID={poID}
                            itemMaster={itemMaster}
                            itemMasterOptions={itemMasterOptions}
                            purchaseTypes={purchaseTypes}
                            sourcingReasons={sourcingReasons}
                            unpurchasedOutcomes={unpurchasedOutcomes}
                            externalSkuOptions={externalSkuOptions}
                            countries={countries}
                            existingWeek={existingWeek}
                            closeModal={() => {
                                setAddItemModalOpen(false);
                                getItemsInTable();
                            }}
                        />
                    )}
                </>
            )}
            <div>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <DataTable
                        columns={columns}
                        initialRowsPerPage={50}
                        data={rows}
                        editable
                        saveEdit={async ({ field, id, row, value }) => {
                            const updates = { [field]: value, poID };
                            if (field === 'qty' && !!showReasonCodeModal) {
                                return showReasonCodeModal(
                                    'purchaseOrderMap',
                                    id,
                                    updates,
                                    getItemsInTable,
                                    parentRow.original.within36HoursOfPickup,
                                );
                            }
                            const response = await api.updatePurchaseOrderMap(poID, id, updates);
                            if (response.status === false) {
                                let message = 'Error modifying purchase order';
                                if (response) {
                                    message += `: ${response.msg}`;
                                }
                                setNotification({ text: message, level: 'error' });
                            } else {
                                setNotification({ text: 'Purchase Order modified!', level: 'success' });
                            }
                            if (response.status === true && (field === 'sku' || field === 'numPallets')) {
                                getItemsInTable();
                            } else {
                                return response.status;
                            }
                        }}
                    />
                )}
            </div>
            {!fromReceiving && (
                <Button
                    onClick={() => {
                        setAddItemModalOpen(true);
                    }}
                >
                    Add Item
                </Button>
            )}
        </div>
    );
}

function AddLotsModal({ closeModal, lotsAlreadyOnASN, parentRow, poID }) {
    const [error, setError] = useState('');
    const [lots, setLots] = useState([]);

    useEffect(() => {
        if (parentRow.original.originFacilityID) {
            getLotsAvailableToTransfer(parentRow.original.originFacilityID);
        }
    }, [parentRow.original.originFacilityID]);

    const getLotsAvailableToTransfer = async (facilityID) => {
        const response = await warehouseApi.getLotsAvailableForTransfer(facilityID);
        if (response.status === false) {
            setLots([]);
            return;
        }

        const data = response.data.rows
            .filter((row) => row.externalSku < 200000)
            .map((row) => {
                row.text = `Lot ${row.lotID} - Ext Sku ${row.externalSku} - ${row.itemNameInternal} - ${row.numAvailableCases} Cases - Exp ${row.expiresOn}`;
                row.value = row.lotID;
                return row;
            });

        setLots(data);
    };

    const itemFormFields = [
        {
            name: 'lotID',
            inputElement: 'autocomplete',
            label: 'Lot',
            gridValue: 12,
            inputProps: {
                required: true,
                opts: lots.filter((lot) => lot.companySource === parentRow.original.companySource),
            },
        },
        {
            name: 'numCases',
            inputElement: 'textField',
            label: 'Case Quantity',
            gridValue: 4,
            inputProps: { type: 'number', integer: true, required: true },
        },
        {
            name: 'numPallets',
            inputElement: 'textField',
            label: 'Pallet Quantity',
            gridValue: 4,
            inputProps: { type: 'number', integer: true, required: true },
        },
        { name: 'week', inputElement: 'textField', label: 'Week number', gridValue: 4, inputProps: { required: true } },
    ];

    const addItemSubmit = async (formData) => {
        const lot = lots.find((item) => item.lotID == formData.lotID);
        if (!lot) {
            return false;
        }

        if (formData.numCases > lot.numAvailableCases) {
            setError(`Lot ${lot.lotID} only has ${lot.numAvailableCases} available cases for transfer`);
            return false;
        }

        if (lotsAlreadyOnASN.find((lotOnASN) => lotOnASN.lotID === lot.lotID)) {
            setError(`Lot ${lot.lotID} is already on this transfer ASN.`);
            return false;
        }

        if (lot.companySource !== parentRow.original.companySource) {
            setError(`Cannot add ${lot.companySource} item to ${parentRow.original.companySource} ASN`);
            return false;
        }

        const item = {
            lotID: lot.lotID,
            externalSku: lot.externalSku,
            itemMasterSku: lot.itemMasterSku,
            name: lot.itemNameInternal,
            numCases: formData.numCases,
            numPallets: formData.numPallets,
            expirationDate: lot.expiresOn,
            unitPrice: lot.unitPrice,
            suggestedRetailPrice: lot.suggestedRetailPrice,
            purchaseType: lot.purchaseType,
            vendorSku: lot.vendorSku,
            week: formData.week,
        };

        const response = await warehouseApi.addItemsToTransferASN(poID, [item]);
        if (response.status === false) {
            let message = 'Error adding item';
            if (response.msg) {
                message += `: ${response.msg}`;
            }
            setError(message);
            return;
        }

        closeModal();
    };

    return (
        <Dialog open onClose={closeModal}>
            <DialogContent>
                <FormComponent formFields={itemFormFields} onSubmit={addItemSubmit} />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </DialogContent>
        </Dialog>
    );
}

// The modal to add items to a purchase order
function AddItemModal({
    closeModal,
    countries,
    existingWeek,
    externalSkuOptions,
    itemMaster,
    itemMasterOptions,
    parentRow,
    poID,
    purchaseTypes,
    reasonCodes,
    sourcingReasons,
    unpurchasedOutcomes,
}) {
    const [error, setError] = useState(null);
    const [hasPrintedExpDate, setHasPrintedExpDate] = useState(false);
    const [hasPrintedProdDate, setHasPrintedProdDate] = useState(false);
    const [purchaseOpts, setPurchaseOpts] = useState(purchaseTypes);
    const [schedDeliveryDate, setSchedDeliveryDate] = useState(parentRow.original.scheduledDeliveryDate);
    const [expProdDate, setExpProdDate] = useState();
    const [expExpireDate, setExpExpireDate] = useState();
    const [purchaseType, setPurchaseType] = useState();
    const [sourcingReason, setSourcingReason] = useState();
    const [unpurchasedOutcome, setUnpurchasedOutcome] = useState();
    const [currentItem, setCurrentItem] = useState();
    const [csPallet, setCsPallet] = useState();
    const [defaultValsLoaded, setDefaultValsLoaded] = useState(false);
    const schedDate = dayjs(schedDeliveryDate);
    const [addItemItemMasterOptions, setAddItemItemMasterOptions] = useState([]);
    const [disableExternalSkuAutoComplete, setDisableExternalSkuAutoComplete] = useState(false);

    useEffect(() => {
        setAddItemItemMasterOptions(itemMasterOptions);
    }, []);

    const handleCheckPrintedExpDate = async (event, value) => {
        if (!value) {
            setDisableExternalSkuAutoComplete(false);
            return;
        }

        const itemSku = value.value;
        const findItem = itemMaster.find((item) => item.sku === itemSku);
        setCurrentItem(findItem);

        if (findItem.expirationType == 'EXPIRATION_DATE') {
            setHasPrintedExpDate(true);
            if (
                schedDeliveryDate &&
                (purchaseType === 'EVERYDAY' || purchaseType === 'PRIVATE_BRAND' || purchaseType === 'SEASONAL')
            ) {
                const schedDate = dayjs(schedDeliveryDate);
                setExpExpireDate(
                    schedDate.clone().add(findItem.vendorGuaranteedDaysUponDelivery, 'days').format('YYYY-MM-DD'),
                );
            } else {
                setExpExpireDate();
            }
        } else {
            setHasPrintedExpDate(false);
            setExpExpireDate();
        }

        if (findItem.expirationType == 'PRODUCTION_DATE') {
            setHasPrintedProdDate(true);
            if (
                schedDeliveryDate &&
                (purchaseType === 'EVERYDAY' || purchaseType === 'PRIVATE_BRAND' || purchaseType === 'SEASONAL')
            ) {
                const schedDate = dayjs(schedDeliveryDate);
                setExpProdDate(
                    schedDate
                        .clone()
                        .add(findItem.vendorGuaranteedDaysUponDelivery, 'days')
                        .subtract(findItem.expireLifeDays, 'days')
                        .format('YYYY-MM-DD'),
                );
            } else {
                setExpProdDate();
            }
        } else {
            setHasPrintedProdDate(false);
            setExpProdDate();
        }

        setDefaultValsLoaded(false);
        setCsPallet(findItem.csPallet);
    };

    useEffect(() => {
        if (
            hasPrintedExpDate &&
            schedDeliveryDate &&
            (purchaseType === 'EVERYDAY' || purchaseType === 'PRIVATE_BRAND' || purchaseType === 'SEASONAL') &&
            currentItem &&
            currentItem.vendorGuaranteedDaysUponDelivery > 0
        ) {
            const schedDate = dayjs(schedDeliveryDate);
            setExpExpireDate(
                schedDate.clone().add(currentItem.vendorGuaranteedDaysUponDelivery, 'days').format('YYYY-MM-DD'),
            );
        } else if (hasPrintedExpDate && purchaseType === 'OPPORTUNISTIC') {
            // Clear auto-populated date for OPPORTUNISTIC - user must provide
            setExpExpireDate();
        } else if (hasPrintedExpDate && !purchaseType) {
            // Clear date if purchaseType is not set yet
            setExpExpireDate();
        }
    }, [purchaseType, schedDeliveryDate, currentItem, hasPrintedExpDate]);

    useEffect(() => {
        if (
            hasPrintedProdDate &&
            schedDeliveryDate &&
            (purchaseType === 'EVERYDAY' || purchaseType === 'PRIVATE_BRAND' || purchaseType === 'SEASONAL') &&
            currentItem &&
            currentItem.vendorGuaranteedDaysUponDelivery > 0
        ) {
            const schedDate = dayjs(schedDeliveryDate);
            setExpProdDate(
                schedDate
                    .clone()
                    .add(currentItem.vendorGuaranteedDaysUponDelivery, 'days')
                    .subtract(currentItem.expireLifeDays, 'days')
                    .format('YYYY-MM-DD'),
            );
        } else if (hasPrintedProdDate && purchaseType === 'OPPORTUNISTIC') {
            // Clear auto-populated date for OPPORTUNISTIC - user must provide
            setExpProdDate();
        } else if (hasPrintedProdDate && !purchaseType) {
            // Clear date if purchaseType is not set yet
            setExpProdDate();
        }
    }, [purchaseType, schedDeliveryDate, currentItem, hasPrintedProdDate]);

    const handleExternalSkuChange = async (event, value) => {
        if (!value) {
            setAddItemItemMasterOptions(itemMasterOptions);
            return;
        }

        setAddItemItemMasterOptions(
            itemMasterOptions.filter((itemMasterOption) => itemMasterOption.externalSku === value.value),
        );
    };

    const handleItemFormChange = (formData) => {
        if (!formData.externalSku && formData.sku) {
            const foundItemMaster = itemMaster.find((item) => item.sku === formData.sku.value);

            formData.externalSku = externalSkuOptions.find((item) => item.value === foundItemMaster.externalSku);

            setAddItemItemMasterOptions(
                itemMasterOptions.filter(
                    (itemMasterOption) => itemMasterOption.externalSku === foundItemMaster.externalSku,
                ),
            );
        }

        if (formData.externalSku && formData.sku) {
            setDisableExternalSkuAutoComplete(true);

            // Load some default values
            if (defaultValsLoaded == false) {
                formData.csPallet = csPallet;
                setDefaultValsLoaded(true);
            }
        }
    };

    const formFields = [
        {
            name: 'externalSku',
            inputElement: 'autocomplete',
            label: 'External SKU (filter)',
            gridValue: 8,
            inputProps: {
                required: false,
                disabled: disableExternalSkuAutoComplete,
                opts: externalSkuOptions,
                onChange: handleExternalSkuChange,
            },
        },
        {
            name: 'sku',
            inputElement: 'autocomplete',
            label: 'Item Master',
            gridValue: 8,
            inputProps: { required: true, opts: addItemItemMasterOptions, onChange: handleCheckPrintedExpDate },
        },
        {
            name: 'qty',
            inputElement: 'textField',
            label: 'Quantity (cases)',
            gridValue: 2,
            inputProps: { required: true },
        },
        {
            name: 'unitPrice',
            inputElement: 'textField',
            label: 'Case Price',
            gridValue: 2,
            inputProps: { required: true },
        },
        { name: 'vendorSku', inputElement: 'textField', label: 'Vendor SKU', gridValue: 4 },
        {
            name: 'csPallet',
            inputElement: 'textField',
            label: 'Cases per pallet',
            gridValue: 2,
            inputProps: { required: true },
        },
        {
            name: 'week',
            inputElement: 'textField',
            label: 'Week',
            gridValue: 2,
            initialValue: existingWeek,
            inputProps: { required: true },
        },
        { name: 'year', inputElement: 'textField', label: 'Year', gridValue: 2 },
        {
            name: 'purchaseType',
            inputElement: 'select',
            label: 'Type',
            gridValue: 4,
            inputProps: {
                opts: purchaseOpts,
                required: true,
                onChange: (e) => setPurchaseType(e.target.value),
            },
        },
        { name: 'suggestedRetailPrice', inputElement: 'textField', label: 'SRP', gridValue: 2 },
        {
            name: 'expectedExpirationDate',
            inputElement: 'date',
            disableTextInput: true,
            label: 'Expected Expiration Date',
            gridValue: 4,
            dependencies: ['sku'],
            inputProps: {
                // Required when expirationType is EXPIRATION_DATE AND purchaseType is OPPORTUNISTIC
                required: isExpirationDateRequired(hasPrintedExpDate, purchaseType),
                disabled: isExpirationDateDisabled(hasPrintedExpDate, purchaseType),
                // Normal/inviting style when OPPORTUNISTIC (user needs to fill it in)
                // Muted style when auto-populated (pre-planned) or not applicable
                style: getExpirationDateFieldStyle(hasPrintedExpDate, purchaseType),
                onChange: (e) => setExpExpireDate(e.target.value),
            },
            value: hasPrintedExpDate ? expExpireDate : undefined,
        },
        {
            name: 'expectedProductionDate',
            inputElement: 'date',
            disableTextInput: true,
            label: 'Expected Production Date',
            gridValue: 4,
            dependencies: ['sku'],
            inputProps: {
                // Required when expirationType is PRODUCTION_DATE AND purchaseType is OPPORTUNISTIC
                required: isProductionDateRequired(hasPrintedProdDate, purchaseType),
                disabled: isProductionDateDisabled(hasPrintedProdDate, purchaseType),
                // Normal/inviting style when OPPORTUNISTIC (user needs to fill it in)
                // Muted style when auto-populated (pre-planned) or not applicable
                style: getProductionDateFieldStyle(hasPrintedProdDate, purchaseType),
                onChange: (e) => setExpProdDate(e.target.value),
            },
            value: hasPrintedProdDate ? expProdDate : undefined,
        },
        {
            name: 'reasonCode',
            label: 'Reason',
            gridValue: 4,
            inputElement: 'select',
            inputProps: { required: true, opts: reasonCodes.filter((item) => item !== 'Carrier Related') },
        },
        {
            name: 'sourcingReasons',
            inputElement: 'autocomplete',
            label: "Why I'm a Misfit",
            gridValue: 4,
            inputProps: {
                multiple: true,
                opts: sourcingReasons,
                required: false,
                onChange: (e) => setSourcingReason(e.target.value),
            },
        },
        {
            name: 'unpurchasedOutcome',
            inputElement: 'select',
            label: 'Where Would It Go',
            gridValue: 4,
            inputProps: {
                opts: unpurchasedOutcomes,
                required: false,
                onChange: (e) => setUnpurchasedOutcome(e.target.value),
            },
        },
        {
            name: 'countryOfOriginId',
            inputElement: 'select',
            label: 'Country of Origin',
            gridValue: 4,
            inputProps: {
                opts: countries,
                required: false,
            },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onFormDataChange={handleItemFormChange}
                    onSubmit={async (formData) => {
                        formData.expectedProductionDate = expProdDate;
                        formData.expectedExpirationDate = expExpireDate;

                        formData.sourcingReasons = formData.sourcingReasons.map((i) => i.value);

                        const items = {};
                        items.items = [formData];

                        const response = await api.addItemToPurchaseOrder(poID, items);
                        if (response.status === false) {
                            let message = 'Error adding item';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                            return;
                        }

                        closeModal();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function EditDeliveryModal({ closeModal, getPurchaseOrders, reasonCodes, rowData }) {
    const [error, setError] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingDate, setPendingDate] = useState(null);
    const [currentDate, setCurrentDate] = useState(dayjs(rowData.scheduledDeliveryDate).format('YYYY-MM-DD'));
    const [formKey, setFormKey] = useState(0); // For forcing re-renders

    const time = dayjs(rowData.scheduledDeliveryDate).format('HH:mm');

    const validateDeliveryDate = (date) => {
        if (!date) return null;

        const deliveryDate = dayjs(date);
        const now = dayjs();

        // Check if date is in the past
        if (deliveryDate.isBefore(now, 'day')) {
            return 'The selected delivery date is in the past. Are you sure you want to proceed?';
        }

        // Check if date is on a weekend (0 = Sunday, 6 = Saturday)
        if (deliveryDate.day() === 0 || deliveryDate.day() === 6) {
            return 'The selected delivery date is on a weekend. Are you sure you want to proceed?';
        }

        return null;
    };

    const formFields = [
        {
            name: 'poID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'Purchase Order ID',
            gridValue: 3,
            inputProps: { readOnly: true },
        },
        {
            name: 'scheduledDeliveryDate',
            initialValue: currentDate,
            inputElement: 'date',
            disableTextInput: true,
            label: 'Scheduled Delivery Date',
            gridValue: 3,
            inputProps: {
                required: true,
                onChange: (e) => {
                    const newDate = e.target.value;
                    setPendingDate(null);
                    const warningMessage = validateDeliveryDate(newDate);

                    if (warningMessage) {
                        setPendingDate(newDate);
                        setShowConfirmation(true);
                        // Prevent the date from being set in the form field
                        e.target.value = currentDate;
                        return;
                    }

                    // If no warning, update the date directly
                    setCurrentDate(newDate);
                },
            },
        },
        {
            name: 'scheduledTime',
            initialValue: time,
            inputElement: 'time',
            label: 'Delivery time',
            gridValue: 3,
            inputProps: { required: true },
        },
        {
            name: 'reasonCode',
            label: 'Reason',
            gridValue: 3,
            inputElement: 'select',
            inputProps: { required: true, opts: reasonCodes },
        },
    ];

    // When currentDate changes, force a full re-render of the form
    useEffect(() => {
        // This will trigger a full re-render of the FormComponent
        setFormKey((prev) => prev + 1);
    }, [currentDate]);

    return (
        <>
            <Modal open onClose={closeModal}>
                <div
                    style={{
                        outline: 0,
                        backgroundColor: 'white',
                        width: '700px',
                        margin: '10% auto',
                        padding: '10px',
                    }}
                >
                    <FormComponent
                        key={`delivery-form-${formKey}`}
                        formFields={formFields}
                        onSubmit={async (formData) => {
                            const item = {};
                            item.scheduledDeliveryDate = formData.scheduledDeliveryDate;
                            item.deliverTime = formData.scheduledTime;
                            item.reasonCode = formData.reasonCode;

                            const response = await api.updatePurchaseOrder(formData.poID, item);
                            if (response.status === false) {
                                let message = 'Error modifying purchase order';
                                if (response) {
                                    message += `: ${response.msg}`;
                                }
                                setError(`Error updating ${message}`);
                            } else {
                                closeModal();
                                getPurchaseOrders();
                            }
                        }}
                    />
                    {error && <div style={{ color: 'red' }}>{error}</div>}
                </div>
            </Modal>

            {pendingDate && (
                <PurchaseOrderGuardrail
                    open={showConfirmation}
                    setOpen={(open) => {
                        setShowConfirmation(open);
                        // If modal is closed without confirmation, clear pending date
                        if (!open && pendingDate) {
                            setPendingDate(null);
                        }
                    }}
                    onSubmit={() => {
                        // Update the scheduled delivery date with the pending date
                        setCurrentDate(pendingDate);
                        // Clear pending date
                        setPendingDate(null);
                    }}
                    message={validateDeliveryDate(pendingDate)}
                />
            )}
        </>
    );
}

function EditPickupDateModal({ closeModal, getPurchaseOrders, reasonCodes, rowData }) {
    const [error, setError] = useState(null);

    const formFields = [
        {
            name: 'poID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'Purchase Order ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'pickupDate',
            inputElement: 'date',
            disableTextInput: false,
            label: 'Pick Up Date',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'reasonCode',
            label: 'Reason',
            gridValue: 4,
            inputElement: 'select',
            inputProps: { required: true, opts: reasonCodes },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '600px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.pickupDate = formData.pickupDate;
                        item.reasonCode = formData.reasonCode;

                        const response = await api.updatePurchaseOrder(formData.poID, item);
                        if (response.status === false) {
                            let message = 'Error modifying purchase order';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                        } else {
                            closeModal();
                        }
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function EditSpecialInstructionsModal({ closeModal, rowData }) {
    const [error, setError] = useState(null);

    const { specialInstructions } = rowData;

    const formFields = [
        {
            name: 'poID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'Purchase Order ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'specialInstructions',
            initialValue: specialInstructions,
            inputElement: 'textField',
            label: 'Special Instructions',
            gridValue: 8,
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.specialInstructions = formData.specialInstructions;

                        const response = await api.updatePurchaseOrder(formData.poID, item);
                        if (response.status === false) {
                            let message = 'Error modifying purchase order';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                        } else {
                            closeModal();
                        }
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function ReasonCodeModal({
    closeModal,
    getPurchaseOrders,
    internalTransfers = false,
    reasonCodeOptions: { id, onSuccess, type, updates, within36HoursOfPickup },
    reasonCodes,
}) {
    const [error, setError] = useState(null);

    let opts = reasonCodes;
    if (updates.removeItem) {
        opts = reasonCodes.filter((item) => item !== 'Carrier Related');
    }

    const formFields = [
        {
            name: 'reasonCode',
            label: 'Reason',
            gridValue: 12,
            inputElement: 'select',
            inputProps: { required: true, opts },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <h4>Select Reason {updates.cancelPO === true && `to Cancel ${internalTransfers ? 'PO' : 'ASN'}`}</h4>
                <FormComponent
                    formFields={formFields}
                    button={{ text: 'Confirm Reason' }}
                    onSubmit={async (formData) => {
                        const { reasonCode } = formData;
                        if (type === 'purchaseOrder') {
                            if (updates.cancelPO === true) {
                                const response = await api.cancelPurchaseOrder(id, reasonCode);
                                if (response.status === false) {
                                    setError(`Failed to cancel PO ${response.msg}`);
                                    return false;
                                }
                                getPurchaseOrders();
                                closeModal();
                            } else {
                                updates.reasonCode = reasonCode;

                                const response = await api.updatePurchaseOrder(id, updates);
                                if (response.status === false) {
                                    let message = 'Error modifying purchase order';
                                    if (response) {
                                        message += `: ${response.msg}`;
                                    }
                                    setError(message);
                                    return false;
                                }
                                getPurchaseOrders();
                                closeModal();
                            }
                        } else if (type === 'purchaseOrderMap') {
                            if (updates.removeItem === true) {
                                const response = await api.removeItemFromPO(updates.poID, id, reasonCode);

                                if (response.status === false) {
                                    setError(`Can't remove item ${response.msg}`);
                                    return false;
                                }
                                getPurchaseOrders();
                                closeModal();
                            } else if (updates.hasOwnProperty('qty')) {
                                // update purchase order item
                                const response = await api.updatePurchaseOrderMap(updates.poID, id, {
                                    qty: updates.qty,
                                    reasonCode,
                                });
                                if (response.status === false) {
                                    let message = 'Error modifying purchase order';
                                    if (response) {
                                        message += `: ${response.msg}`;
                                    }
                                    setError(message);
                                    return false;
                                }
                                getPurchaseOrders();
                                closeModal();
                            }
                        }

                        if (onSuccess && onSuccess instanceof Function) {
                            onSuccess();
                        }
                        return true;
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

// The main PO view
function ViewPO({ internalTransfers = false }) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [editDeliveryModalOpen, setEditDeliveryModalOpen] = useState(false);
    const [editSpecialInstructionsModalOpen, setEditSpecialInstructionsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [vendors, setVendors] = useState([]);
    const [shippers, setShippers] = useState([]);
    const [crossDocks, setCrossDocks] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [buyers, setBuyers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [purchaseTypes, setPurchaseTypes] = useState([]);
    const [poLookup, setPoLookup] = useState(null);
    const [searchObj, setSearchObj] = useState({});
    const [reasonCodes, setReasonCodes] = useState([]);
    const [reasonCodeModalOpen, setReasonCodeModalOpen] = useState(false);
    const [reasonCodeOptions, setReasonCodeOptions] = useState({});
    const [receivingASN, setReceivingASN] = useState(false);
    const [sourcingReasons, setSourcingReasons] = useState([]);
    const [unpurchasedOutcomes, setUnpurchasedOutcomes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [deptLeadCheck, setDeptLeadCheck] = useState(false);
    const [pickupDateModalOpen, setPickupDateModalOpen] = useState(false);

    const user = useContext(UserContext);

    // Load countries
    const getCountries = async () => {
        const response = await addressesApi.getCountries();
        if (!response.status) {
            setNotification({ text: 'Failed to get countries', level: 'error' });
            return;
        }

        const countriesAsOpts = response.data.rows.map((row) => {
            row.text = `${row.name} (${row.countryCode})`;
            row.value = row.id;
            return row;
        });

        setCountries(countriesAsOpts);
    };

    const getFacilities = async () => {
        const response = await warehouseApi.getFacilities(true);
        if (response.status === false) {
            return;
        }

        const facilitiesAsOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            return row;
        });

        facilitiesAsOpts.unshift({ text: 'Please Select', value: 0 });

        setFacilities(facilitiesAsOpts);
    };

    const getBuyers = async () => {
        const response = await api.getBuyers();
        if (response.status === false) {
            setNotification({ text: `Failed to get buyers: ${response.msg}`, level: 'error' });
            setBuyers([]);
            return;
        }

        const buyerOpts = response.data.rows.map((row) => ({
            text: `${row.firstName} ${row.lastName}`,
            value: row.id,
        }));
        setBuyers(buyerOpts);
    };

    const getItemTypes = async () => {
        const res = await api.getPurchaseOrderItemTypes();
        if (res.status === false) {
            return;
        }

        setPurchaseTypes(res.data.types);
    };

    const getReasonCodes = async () => {
        const res = await api.getPOUpdateReasonCodes();
        if (res.status === false) {
            return;
        }

        setReasonCodes(res.data.reasonCodes);
    };

    const getSourcingReasons = async () => {
        const resp = await api.getSourcingReasons();
        if (resp.status) {
            setSourcingReasons(resp.data.reasons.map((r) => r.name));
        }
    };

    const getUnpurchasedOutcomes = async () => {
        const resp = await api.getUnpurchasedOutcomes();
        if (resp.status) {
            setUnpurchasedOutcomes(resp.data.outcomes.map((o) => o.displayName));
        }
    };

    const processPORow = (row) => {
        const now = dayjs();
        // Convert UTC to local timezone and format
        row.createdAt = dayjs.utc(row.createdAt.date).local().format('MM/DD/YYYY');
        row.pickupDate = dayjs(row?.pickupDate?.date).format('MM/DD/YYYY');
        row.scheduledDeliveryDate = dayjs(row.scheduledDeliveryDate.date).format('MM/DD/YYYY HH:mm:ss');
        if (row.crossDockDeliveryDate) {
            row.crossDockDeliveryDate = dayjs(row.crossDockDeliveryDate.date).format('YYYY-MM-DD');
        }
        row.buyersWithNames = row.buyers;
        row.buyers = row.buyers.map((item) => item.authUserID);
        const pickupDiff = dayjs.duration(dayjs(row.pickupDate).diff(now));
        row.within36HoursOfPickup =
            pickupDiff.asHours() <= 36 && row.status === 'PLACED' && row.deliveryType === 'PICK UP';
        return row;
    };

    const markPurchaseOrderAsSentToVendor = (row) => {
        setRows(
            rows.map((r) => {
                if (r.id === row.original.id) {
                    const dt = dayjs().format('MMM Do, YYYY [at] HH:mm:ss A');
                    r.sentToVendor = dt;
                }

                return r;
            }),
        );
    };

    // Load purchase orders
    const getPurchaseOrders = async () => {
        if (!rows.length) {
            setLoading(true);
        }
        let facilityID = user.getFacilityID();
        if (searchObj.hasOwnProperty('facilityID')) {
            facilityID = searchObj.facilityID;
        }

        const externalSku = searchObj.hasOwnProperty('externalSku') ? searchObj.externalSku : null;
        const poID = searchObj.hasOwnProperty('poID') ? searchObj.poID : null;

        let response;
        if (internalTransfers) {
            response = await warehouseApi.getInternalTransfers(externalSku, poID);
        } else {
            const ALL_FACILITIES = 0;
            response = await api.getPurchaseOrders(ALL_FACILITIES, externalSku, poID);
        }

        if (response.status === false) {
            setNotification({ text: `Can't access POs: ${response.msg}`, level: 'error' });
            setLoading(false);
            return;
        }

        if (response.data.rows) {
            response.data.rows.map(processPORow);
        }

        let data = response.data.rows.map((row) => ({
            ...row,
            canUserSelectBulkAction:
                response.data.deptLeadCheck ||
                (!response.data.deptLeadCheck && row.poTotalCost <= PO_PREVENT_VENDOR_SEND),
        }));

        // filter out duplicate PO ID records
        // that are on more than one fob truck
        data = data.filter((value, index, self) => index === self.findIndex((t) => t.id === value.id));

        setRows(data);
        setDeptLeadCheck(response.data.deptLeadCheck);
        setLoading(false);
    };

    useEffect(() => {
        getPurchaseOrders();
    }, [searchObj]);

    useEffect(() => {
        getFacilities();
        getVendors();
        getShippers();
        getBuyers();
        getItemTypes();
        getReasonCodes();
        getSourcingReasons();
        getUnpurchasedOutcomes();
        getCountries();
        getCrossDocks();
    }, []);

    // Load vendors
    let getVendors = async () => {
        const response = await api.getVendors();
        if (response.status === false) {
            setNotification({ text: `No vendors found ${response.msg}`, level: 'error' });
            setVendors([]);
            return;
        }

        const vendorAsOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            return row;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setVendors([pleaseSelect].concat(vendorAsOpts));
    };

    // Load shippers
    let getShippers = async () => {
        const response = await api.getShippers();
        if (response.status === false) {
            setNotification({ text: `No vendors found ${response.msg}`, level: 'error' });
            setShippers([]);
            return;
        }

        const shippersAsOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            return row;
        });

        const pleaseSelect = { text: 'Please Select', value: '' };
        setShippers([pleaseSelect].concat(shippersAsOpts));
    };

    let getCrossDocks = async () => {
        const response = await logisticsApi.getInboundCrossDocks(true);
        if (response.status === false) {
            setNotification({ text: 'Failed to get cross docks', level: 'error' });
            setCrossDocks([]);
            return;
        }

        const crossDocksAsOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            return row;
        });

        const pleaseSelect = { text: 'Please Select', value: '' };
        setCrossDocks([pleaseSelect].concat(crossDocksAsOpts));
    };

    const showDeliveryEditModal = (row) => {
        setSelectedRow(row);
        setEditDeliveryModalOpen(true);
    };

    const showSpecialInstructionsEditModal = (row) => {
        setSelectedRow(row);
        setEditSpecialInstructionsModalOpen(true);
    };

    const showReasonCodeModal = (type, id, updates, onSuccess, within36HoursOfPickup) => {
        setReasonCodeOptions({ type, id, updates, onSuccess, within36HoursOfPickup });
        setReasonCodeModalOpen(true);
    };

    const showEditPickupDateModal = (row) => {
        setSelectedRow(row);
        setPickupDateModalOpen(true);
    };

    const receiveImperfectInternalTransfer = async (poID) => {
        setReceivingASN(true);
        const res = await receivingApi.receiveTransferASNAtImperfectFacility(poID);
        if (!res.status) {
            setNotification({ text: res.msg, level: 'Error' });
        }
        setNotification({ text: 'ASN Received', level: 'success' });
        getPurchaseOrders();
        setReceivingASN(false);
    };

    // CHANGE HAS TO BE HERE
    const imperfectFacilityIds = [9, 10, 11, 12, 13];
    const columns = [
        {
            Header: 'Receive',
            hide: !(imperfectFacilityIds.includes(user.getFacilityID()) && internalTransfers),
            Cell: ({ row }) => {
                if (
                    imperfectFacilityIds.includes(row.original.destinationFacilityID) &&
                    (row.original.status === 'PLACED' || row.original.status === 'OPEN') &&
                    row.original.receivableAtIFWithoutQC &&
                    row.original.destinationFacilityID === user.getFacilityID()
                ) {
                    return (
                        <Button
                            disabled={receivingASN}
                            onClick={() => {
                                receiveImperfectInternalTransfer(row.original.id);
                            }}
                        >
                            Receive
                        </Button>
                    );
                }
                return null;
            },
        },
        { accessor: 'id', Header: internalTransfers ? 'ASN #' : 'PO #' },
        {
            accessor: 'createdAt',
            Header: `Created On`,
        },
        { accessor: 'status', Header: 'Status', filterType: 'multiSelect' },
        { accessor: 'truckID', Header: 'Truck ID' },
        {
            accessor: 'deliveryType',
            Header: 'Delivery Type',
            hide: internalTransfers,
            editable: true,
            editProps: {
                type: 'select',
                options: [
                    { value: '', text: 'Please Select' },
                    { value: 'PICK UP', text: 'PICK UP' },
                    { value: 'DELIVERED', text: 'DELIVERED' },
                ],
            },
        },
        {
            accessor: 'pickupNumber',
            Header: 'Pick Up #',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'text',
            },
        },
        {
            accessor: 'pickupDate',
            Header: 'Pick Up Date',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'date',
                required: false,
                disableTextInput: true,
            },
            customEdit: showEditPickupDateModal,
        },
        {
            accessor: 'scheduledDeliveryDate',
            Header: 'Scheduled Delivery',
            editable: true,
            customEdit: showDeliveryEditModal,
        },
        {
            accessor: 'vendorName',
            Header: 'Vendor',
            hide: internalTransfers,
            editable: true,
            editProps: {
                type: 'select',
                options: vendors,
            },
        },
        {
            accessor: 'shipperID',
            Header: 'Shipper',
            hide: internalTransfers,
            editable: true,
            editProps: {
                type: 'select',
                options: shippers,
                required: false,
            },
        },
        {
            accessor: 'crossDockID',
            Header: 'Cross Dock',
            editable: true,
            editProps: {
                type: 'select',
                options: crossDocks,
                required: false,
            },
        },
        {
            accessor: 'crossDockDeliveryDate',
            Header: 'Cross Dock Delivery Date',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'date',
                disableTextInput: true,
                required: false,
            },
        },
        {
            accessor: 'facilityName',
            Header: 'Facility',
            editable: true,
            hide: internalTransfers,
            editProps: {
                type: 'select',
                options: facilities,
            },
        },
        {
            accessor: 'originFacilityID',
            Header: 'Origin Facility',
            editable: false,
            hide: !internalTransfers,
            editProps: {
                type: 'select',
                options: facilities,
            },
        },
        {
            accessor: 'destinationFacilityID',
            Header: 'Destination Facility',
            editable: false,
            hide: !internalTransfers,
            editProps: {
                type: 'select',
                options: facilities,
            },
        },
        { accessor: 'poTotalCost', Header: 'Total Cost' },
        {
            accessor: 'buyers',
            Header: internalTransfers ? 'Procurement Stakeholders' : 'Buyers',
            editable: true,
            filterValues: (row) => row.original.buyersWithNames.map((item) => item.name),
            editProps: {
                type: 'autocomplete-multiple',
                options: buyers,
                multiple: true,
            },
        },
        {
            accessor: 'minTempReq',
            Header: 'Min Temperature',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                integer: true,
            },
        },
        {
            accessor: 'maxTempReq',
            Header: 'Max Temperature',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                integer: true,
            },
        },
        {
            accessor: 'specialInstructions',
            Header: 'Special Instructions',
            editable: true,
            customEdit: showSpecialInstructionsEditModal,
            width: '150px',
        },
        {
            accessor: 'download',
            Header: 'Download',
            Cell: ({ cell: { value: initialValue }, row }) => {
                const [downloadOpt, setDownloadOpt] = useState('pdf');
                const downloadOptions = ['csv', 'pdf', 'ship-list'];
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <NativeSelect
                            id={`Download_Select_${row.id}`}
                            className="input-element-select"
                            value={downloadOpt}
                            label="Download"
                            onChange={(event) => {
                                setDownloadOpt(event.target.value);
                            }}
                        >
                            {downloadOptions.map((value, i) => (
                                <option key={i} value={value}>
                                    {value}
                                </option>
                            ))}
                            ;
                        </NativeSelect>
                        <Button
                            id={`Download_${row.id}`}
                            onClick={() => {
                                if (downloadOpt === 'csv') {
                                    api.downloadPOCSV(row.original.id, row.original.vendorName);
                                } else if (downloadOpt === 'pdf') {
                                    api.downloadPOPDF(
                                        row.original.id,
                                        row.original.vendorName,
                                        row.original.facilityName,
                                    );
                                } else if (downloadOpt === 'ship-list') {
                                    api.downloadPOShipList(row.original.id);
                                } else {
                                    console.log(`Unrecognized download option ${downloadOpt}`);
                                }
                            }}
                        >
                            Download
                        </Button>
                    </div>
                );
            },
        },
        {
            accessor: 'cancel',
            Header: 'Cancel',
            Cell: ({ cell: { value: initialValue }, row }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`Cancel_${row.id}`}
                        onClick={async () => {
                            showReasonCodeModal(
                                'purchaseOrder',
                                row.original.id,
                                { cancelPO: true },
                                null,
                                row.original.within36HoursOfPickup,
                            );
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'sendToVendor',
            Header: 'Send To Vendor',
            hide: internalTransfers,
            Cell: ({ cell: { value: initialValue }, row }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`Send_${row.id}`}
                        disabled={!deptLeadCheck && row.original.poTotalCost >= PO_PREVENT_VENDOR_SEND}
                        onClick={async () => {
                            const response = await api.sendPurchaseOrderToVendor(row.original.id);
                            if (response.status === false) {
                                setNotification({ text: `Failed to send PO ${response.msg}`, level: 'error' });
                                return;
                            }
                            markPurchaseOrderAsSentToVendor(row);
                            setNotification({ text: 'Sent PO to Vendor', level: 'success' });
                        }}
                    >
                        SEND
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'sentToVendor',
            Header: 'Sent',
            Cell: ({ cell: { value: initialValue } }) => {
                const dt = dayjs(initialValue).format('MMM Do, YYYY [at] HH:mm:ss A');

                return (
                    <Box display="flex" flex="1" justifyContent="start" alignItems="center">
                        <Box>{initialValue ? 'Yes' : 'No'}</Box>

                        {initialValue && (
                            <Tooltip title={`Sent: ${dt}`}>
                                <InfoOutlined style={{ cursor: 'pointer', marginLeft: '.675rem' }} />
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
        { accessor: 'priority', Header: 'Priority', type: 'checkbox', editable: true, editProps: { type: 'checkbox' } },
        {
            accessor: 'estimatedFreightCost',
            Header: 'Freight Cost',
            editable: (row) => row.original.deliveryType == 'DELIVERED',
            editProps: { type: 'input', inputType: 'number' },
        },
        { accessor: 'produceOrGrocery', Header: 'Produce/Grocery' },
    ];

    const searchPOs = async ({ searchBy, searchTerm }) => {
        if (searchBy === 'poID') {
            // find this PO, if in other facility, allow easy switch
            const poID = searchTerm;
            setPoLookup(null);

            const res = await api.getPurchaseOrderDetails(poID);
            if (!res.status) {
                setNotification({ text: `Failed to lookup po: ${res.msg}`, level: 'error' });
                return;
            }

            const facilityID = res.data?.output?.facilityID;
            if (!facilityID) {
                setNotification({ text: `PO ${poID} not found`, level: 'error' });
                return;
            }
            let facilityName = null;
            if (facilityID) {
                facilityName = facilities.find((item) => item.value === facilityID)?.text;
            }
            // If the facilityID was not found in facilities, that means it's old/inactive
            if (!facilityName) {
                setNotification({ text: `PO ${poID} found in an inactive facility`, level: 'error' });
                return;
            }
            setPoLookup({ poID, facilityID, facilityName });
            if (user.getFacilityClusterIds().includes(facilityID)) {
                setSearchObj({ poID });
            }
        } else if (searchBy === 'externalSku') {
            setPoLookup({});
            setSearchObj({ externalSku: searchTerm });
        }
    };

    const searchByOptions = [{ value: 'externalSku', text: 'External Sku' }];
    if (!internalTransfers) {
        searchByOptions.unshift({ value: 'poID', text: 'PO Number' });
    }

    const bulkActions = [
        {
            name: 'Send Emails to Vendors',
            action: async (orderIDs) => {
                const promises = orderIDs.map((id) => api.sendPurchaseOrderToVendor(id));
                const res = await Promise.all(promises);
                if (!res.every((item) => item.status)) {
                    setNotification({ text: `Issue sending the selected POs to vendors. ${res.msg}`, level: 'error' });
                    return;
                }
                setNotification({ text: 'Sent PO(s) to Vendor(s)', level: 'success' });
            },
        },
        {
            name: 'Download pre-populated csv',
            action: async (orderIDs) => {
                const obj = {};
                obj.poIDs = orderIDs;
                const res = await api.downloadPrePopulatedCsv(obj);
                if (!res.status) {
                    setNotification({ text: `Failed download csv file: ${res.msg}`, level: 'error' });
                }
            },
        },
        {
            name: 'Download PDFs',
            action: async (orderIDs) => {
                if (orderIDs.length === 0) {
                    setNotification({ text: 'No POs selected for PDF download.', level: 'info' });
                    return;
                }

                let successCount = 0;
                let errorCount = 0;

                for (const id of orderIDs) {
                    const poRow = rows.find((row) => row.id === id);
                    if (poRow) {
                        try {
                            // Assuming api.downloadPOPDF triggers a download.
                            // If it's async and returns a promise, await is appropriate.
                            await api.downloadPOPDF(poRow.id, poRow.vendorName, poRow.facilityName);
                            successCount++;
                        } catch (error) {
                            errorCount++;
                            console.error(`Error downloading PDF for PO ${poRow.id}:`, error);
                            // Update notification for the specific error
                            setNotification({ text: `Error downloading PDF for PO ${poRow.id}.`, level: 'error' });
                        }
                    } else {
                        errorCount++;
                        setNotification({
                            text: `Could not find data for PO ID ${id} to download PDF.`,
                            level: 'error',
                        });
                    }
                }

                if (successCount > 0 && errorCount === 0) {
                    setNotification({
                        text: `Successfully initiated download for ${successCount} PDF(s).`,
                        level: 'success',
                    });
                } else if (successCount > 0 && errorCount > 0) {
                    setNotification({
                        text: `Initiated download for ${successCount} PDF(s). Failed to initiate for ${errorCount} PDF(s).`,
                        level: 'warning',
                    });
                } else if (errorCount > 0 && successCount === 0) {
                    setNotification({ text: `Failed to initiate download for ${errorCount} PDF(s).`, level: 'error' });
                }
                // No specific return needed for the action itself
            },
        },
    ];

    return (
        <div>
            <h1>{internalTransfers ? 'Transfer ASNS' : 'Purchase Orders'}</h1>
            <Notifications options={notification} />
            <div>
                {editDeliveryModalOpen && (
                    <EditDeliveryModal
                        rowData={selectedRow}
                        getPurchaseOrders={getPurchaseOrders}
                        closeModal={() => {
                            setEditDeliveryModalOpen(false);
                        }}
                        reasonCodes={reasonCodes}
                    />
                )}
                {reasonCodeModalOpen && (
                    <ReasonCodeModal
                        reasonCodes={reasonCodes}
                        reasonCodeOptions={reasonCodeOptions}
                        getPurchaseOrders={getPurchaseOrders}
                        internalTransfers={internalTransfers}
                        closeModal={() => {
                            setReasonCodeModalOpen(false);
                            setReasonCodeOptions({});
                        }}
                    />
                )}
                {pickupDateModalOpen && (
                    <EditPickupDateModal
                        rowData={selectedRow}
                        reasonCodes={reasonCodes}
                        closeModal={() => {
                            setPickupDateModalOpen(false);
                            getPurchaseOrders();
                        }}
                    />
                )}
                {editSpecialInstructionsModalOpen && (
                    <EditSpecialInstructionsModal
                        rowData={selectedRow}
                        closeModal={() => {
                            setEditSpecialInstructionsModalOpen(false);
                            getPurchaseOrders();
                        }}
                    />
                )}
                <div>
                    <div style={{ margin: '0 auto' }}>
                        <Grid container>
                            <Grid item xs={12}>
                                <SearchBar
                                    searchByOptions={searchByOptions}
                                    onSubmit={searchPOs}
                                    onClearSearch={() => {
                                        setPoLookup({});
                                        setSearchObj({});
                                    }}
                                />
                            </Grid>
                            {poLookup && poLookup.poID && poLookup.facilityID && (
                                <>
                                    <Grid item xs={6}>
                                        <div>
                                            PO {poLookup.poID} found in facility {poLookup.facilityName}
                                        </div>
                                    </Grid>

                                    <Grid item xs={6}>
                                        {!user.getFacilityClusterIds().includes(poLookup.facilityID) && (
                                            <Button
                                                variant="outlined"
                                                onClick={async () => {
                                                    user.setFacilityIDAndName({
                                                        facilityID: poLookup.facilityID,
                                                        facilityName: poLookup.facilityName,
                                                    });
                                                    setSearchObj({
                                                        facilityID: poLookup.facilityID,
                                                        poID: poLookup.poID,
                                                    });
                                                }}
                                            >
                                                Change Facility
                                            </Button>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </div>
                </div>
                <div>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <DataTable
                            selectMultiple
                            columns={columns}
                            data={rows}
                            expandable
                            bulkActions={bulkActions}
                            tableContainerMaxHeight={800}
                            stickyHeader
                            initialRowsPerPage={10}
                            ExpansionComponent={
                                <ViewItemsTable
                                    reasonCodes={reasonCodes}
                                    showReasonCodeModal={showReasonCodeModal}
                                    purchaseTypes={purchaseTypes}
                                    internalTransfers={internalTransfers}
                                    sourcingReasons={sourcingReasons}
                                    unpurchasedOutcomes={unpurchasedOutcomes}
                                    countries={countries}
                                />
                            }
                            editable
                            saveEdit={async ({ field, id, row, value }) => {
                                const item = {};
                                item[field] = value;
                                if (field === 'vendorName') {
                                    item.vendorID = value;
                                } else if (field === 'shipperID') {
                                    item.inboundShipperID = value;
                                } else if (field === 'facilityName') {
                                    item.facilityID = value;
                                }

                                if (['pickupDate', 'shipperID', 'deliveryType'].includes(field)) {
                                    showReasonCodeModal('purchaseOrder', id, item, null, row.within36HoursOfPickup);
                                    return;
                                }

                                const response = await api.updatePurchaseOrder(id, item);
                                if (response.status === false) {
                                    let message = 'Error modifying purchase order';
                                    if (response) {
                                        message += `: ${response.msg}`;
                                    }
                                    setNotification({ text: message, level: 'error' });
                                } else {
                                    setNotification({ text: 'Purchase Order modified!', level: 'success' });
                                    await getPurchaseOrders();
                                }

                                return response.status;
                            }}
                            duplicateRowSettings={{
                                enable: !internalTransfers,
                                CustomFormComponent: <CreatePO />,
                                fetchAdditionalData: async (row) => {
                                    const response = await api.getPurchaseOrderDetails(row.id);
                                    if (!response.status) {
                                        setNotification({
                                            text: 'Failed to get items in purchase order',
                                            level: 'error',
                                        });
                                        return [];
                                    }
                                    const poDetails = response.data.output.items.map((row) => {
                                        row.id = row.poMapID;
                                        row.unitPrice = parseFloat(row.unitPrice).toFixed(2);
                                        return row;
                                    });
                                    return poDetails;
                                },
                                onSuccess: () => {
                                    getPurchaseOrders();
                                    setNotification({ text: 'Purchase Order(s) Created', level: 'success' });
                                },
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export { ViewPO, ViewItemsTable };
